import React from "react";
import { Box, BoxProps, Theme, useMediaQuery } from "@mui/material";
import { useMandatorContext } from "@bn/theming";
import BnLogo from "./images/bn-logo.svg?url";
import BnLogoCompact from "./images/bn-logo-compact.svg?url";
import BnLogoWhite from "./images/bn-logo-white.svg?url";
import ArivaLogo from "./images/ariva-logo.svg?url";
import ArivaLogoCompact from "./images/ariva-logo-compact.svg?url";
import WsoLogo from "./images/wso-logo.svg?url";
import WsoCompactLogo from "./images/wso-compact-logo.png?url";

export type LogoVariant =
  | "default"
  | "compact"
  | "superCompact"
  | "bankConnection"
  | "sidebar"
  | "footer"
  | "agb"
  | "asset";

type LogoVariantProps = {
  icon: string;
  width: number | string;
  height: number | string;
  breakpoints?: {
    [key: string]: {
      width: number | string;
      height: number | string;
    };
  };
};

const boersennewsLogos: Partial<Record<LogoVariant, LogoVariantProps>> = {
  default: { icon: BnLogo, width: 100, height: 47 },
  asset: { icon: BnLogoCompact, width: 15, height: 21.5 },
  superCompact: { icon: BnLogoCompact, width: 30, height: 43 },
  compact: { icon: BnLogoCompact, width: 30, height: 43 },
  bankConnection: { icon: BnLogoCompact, width: 24, height: 24 },
  sidebar: {
    icon: BnLogoWhite,
    width: 89,
    height: 43,
    breakpoints: { sm: { width: 85, height: 32 } },
  },
  footer: { icon: BnLogoWhite, width: 89, height: 32 },
  agb: {
    icon: BnLogoWhite,
    width: 105,
    height: 43,
    breakpoints: { md: { width: 88, height: 32 } },
  },
};

const wsoLogos: Partial<Record<LogoVariant, LogoVariantProps>> = {
  default: { icon: WsoLogo, width: 140, height: 18 },
  compact: { icon: WsoCompactLogo, width: 30, height: 30 },
  superCompact: { icon: WsoCompactLogo, width: 30, height: 30 },
  bankConnection: { icon: WsoCompactLogo, width: 24, height: 24 },
  asset: { icon: WsoCompactLogo, width: 22, height: 22 },
  sidebar: {
    icon: WsoLogo,
    width: 120,
    height: 16,
    breakpoints: { sm: { width: 100, height: 13 } },
  },
  footer: { icon: WsoLogo, width: 120, height: 16 },
  agb: {
    icon: WsoLogo,
    width: 140,
    height: 18,
    breakpoints: { md: { width: 120, height: 16 } },
  },
};

const arivaLogos: Partial<Record<LogoVariant, LogoVariantProps>> = {
  default: { icon: ArivaLogo, width: 150, height: 30 },
  asset: { icon: ArivaLogoCompact, width: 22, height: 22 },
  superCompact: { icon: ArivaLogoCompact, width: 32, height: 32 },
  compact: { icon: ArivaLogoCompact, width: 32, height: 32 },
  bankConnection: { icon: ArivaLogoCompact, width: 24, height: 24 },
  sidebar: {
    icon: ArivaLogo,
    width: 130,
    height: 26,
    breakpoints: { sm: { width: 110, height: 22 } },
  },
  footer: { icon: ArivaLogo, width: 130, height: 26 },
  agb: {
    icon: ArivaLogo,
    width: 150,
    height: 30,
    breakpoints: { md: { width: 130, height: 26 } },
  },
};

const logosByMandator: Partial<
  Record<string, Partial<Record<LogoVariant, LogoVariantProps>>>
> = {
  ariva_de: arivaLogos,
  wso: wsoLogos,
};

const getLogoProps = (
  variant: LogoVariant,
  mandatorSlug: string
): LogoVariantProps | undefined =>
  (logosByMandator[mandatorSlug] ?? boersennewsLogos)[variant];

type Props = {
  variant?: LogoVariant;
  alt?: string;
} & BoxProps;

export const Logo = ({ variant = "default", alt, ...rest }: Props) => {
  const { mandatorSlug } = useMandatorContext();
  const logoVariant =
    getLogoProps(variant, mandatorSlug) ||
    getLogoProps(
      variant === "superCompact" ? "compact" : "default",
      mandatorSlug
    );
  const logoUrl = logoVariant?.icon;
  const isLessSm = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const isLessMd = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  const dimensions =
    isLessSm && logoVariant?.breakpoints?.sm
      ? logoVariant?.breakpoints?.sm
      : isLessMd && logoVariant?.breakpoints?.md
        ? logoVariant?.breakpoints?.md
        : logoVariant;
  const { width, height } = dimensions || {};
  return logoUrl ? (
    <Box width={width} height={height} {...rest} sx={{ cursor: "pointer" }}>
      <img
        width="100%"
        height="100%"
        data-testid={`logo-${mandatorSlug || "unknown"}-${variant}`}
        style={{ objectFit: "contain" }}
        src={logoUrl}
        alt={alt || `${mandatorSlug || "Boersenews"} logo`}
      />
    </Box>
  ) : null;
};
